import {Box, SegmentedControl, IconButton, Text, useResponsiveValue} from '@primer/react'
import {XIcon, SidebarCollapseIcon, TrashIcon} from '@primer/octicons-react'
import {Tooltip} from '@primer/react/next'
import {Dialog} from '@primer/react/experimental'
import {
  usePlaygroundState,
  usePlaygroundManager,
  SidebarSelectionOptions,
  type ModelState,
} from '../../../utils/playground-manager'
import ModelInfo from './ModelInfo'
import ModelParameters from './ModelParameters'

export function PlaygroundInputs({model, position}: {model: ModelState; position: number}) {
  const isMobile = useResponsiveValue({narrow: true}, false)
  const manager = usePlaygroundManager()
  const state = usePlaygroundState()
  const onComparisonMode = state.models.length > 1

  const resetLabel = 'Reset to default inputs'

  if (onComparisonMode) {
    return null
  }

  if (isMobile) {
    return (
      <>
        {state.showParametersOnMobile && isMobile && (
          <Dialog
            position={{narrow: 'fullscreen', regular: 'center'}}
            renderHeader={() => {
              return (
                <Box
                  as="header"
                  sx={{
                    borderBottomColor: 'border.default',
                    borderBottomStyle: 'solid',
                    borderBottomWidth: 1,
                    py: 2,
                    pr: 2,
                    pl: 3,
                    alignItems: 'center',
                    display: 'flex',
                  }}
                >
                  <Text as="h1" sx={{fontSize: 1, fontWeight: 'bold', flex: 1}}>
                    {state.tab === SidebarSelectionOptions.DETAILS ? 'Info' : 'Parameters'}
                  </Text>
                  <Box sx={{display: 'flex'}}>
                    {state.tab === SidebarSelectionOptions.PARAMETERS && model.parametersHasChanges && (
                      <Tooltip text={resetLabel}>
                        <IconButton
                          icon={TrashIcon}
                          as="button"
                          variant="invisible"
                          aria-label={resetLabel}
                          onClick={() => manager.resetAllInputs(position, model)}
                        />
                      </Tooltip>
                    )}
                    <Tooltip text="Close">
                      <IconButton
                        icon={XIcon}
                        as="button"
                        variant="invisible"
                        aria-label="Close"
                        onClick={() => manager.setShowParametersOnMobile(false)}
                      />
                    </Tooltip>
                  </Box>
                </Box>
              )
            }}
            onClose={() => manager.setShowParametersOnMobile(false)}
          >
            <Content activeTab={state.tab} model={model} position={position} />
          </Dialog>
        )}
      </>
    )
  }

  const smallSizeForSegmentControl = {
    height: '28px',
    fontSize: 1,
  }

  return (
    <Box
      sx={{
        height: '100%',
        flexDirection: 'column',
        width: '30%',
        maxWidth: '480px',
        minWidth: '280px',
        overflow: 'auto',
        display: state.showParameters ? ['none', 'none', 'flex'] : 'none',
      }}
      className="border-top border-md-top-0 border-md-left"
    >
      <div
        className="d-flex flex-justify-between position-sticky top-0 p-2 border-bottom bgColor-muted flex-items-center"
        style={{zIndex: 1}}
      >
        <div className="flex-1 pr-2">
          <SegmentedControl
            sx={smallSizeForSegmentControl}
            size="small"
            onChange={selectedIndex => {
              manager.setTab(selectedIndex)
            }}
            fullWidth={{narrow: true, regular: false}}
            aria-label="Mode"
          >
            <SegmentedControl.Button selected={state.tab === SidebarSelectionOptions.PARAMETERS}>
              Parameters
            </SegmentedControl.Button>
            <SegmentedControl.Button selected={state.tab === SidebarSelectionOptions.DETAILS}>
              Details
            </SegmentedControl.Button>
          </SegmentedControl>
        </div>
        <div>
          {state.tab === SidebarSelectionOptions.PARAMETERS && model.parametersHasChanges && (
            <Tooltip text={resetLabel}>
              <IconButton
                size="small"
                icon={TrashIcon}
                as="button"
                aria-label={resetLabel}
                onClick={() => manager.resetAllInputs(position, model)}
              />
            </Tooltip>
          )}
          <IconButton
            size="small"
            icon={SidebarCollapseIcon}
            aria-label="Hide parameters setting"
            onClick={() => {
              manager.setShowParameters(false)
            }}
            className="ml-2"
          />
        </div>
      </div>

      <Content activeTab={state.tab} model={model} position={position} />
    </Box>
  )
}

function Content({activeTab, model, position}: {activeTab: number; model: ModelState; position: number}) {
  return (
    <>
      {activeTab === SidebarSelectionOptions.DETAILS && (
        <div>
          <ModelInfo model={model.catalogData} />
        </div>
      )}

      {activeTab === SidebarSelectionOptions.PARAMETERS && <ModelParameters model={model} position={position} />}
    </>
  )
}

try{ PlaygroundInputs.displayName ||= 'PlaygroundInputs' } catch {}
try{ Content.displayName ||= 'Content' } catch {}